import React, { FC, memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import UserAuthService from '../../../services/user-auth';
import Router from '../../../navigation/router';
import { routes } from '../../../navigation';
import Navigation from './Navigation';
import styles from './assets/header.module.scss';

import LogoutConfirmationModal from '../../common/LogoutConfirmationModal';

const getPageTitle = (pathname: string): string => {
    if (pathname.startsWith('/user')) return 'User Management';
    if (pathname.startsWith('/daily-inspiration')) return 'Daily Inspirations';
    if (pathname.startsWith('/bible-quote')) return 'God Speaks / Bible';
    if (pathname.startsWith('/date-night')) return 'Date Night Ideas';
    if (pathname.startsWith('/conversation-starter-category')) return 'Conversation Starter Categories';
    if (pathname.startsWith('/conversation-starter')) return 'Conversation Starters';
    if (pathname.startsWith('/article')) return 'Articles';
    if (pathname.startsWith('/assessment-questions')) return 'Assessment Questions';
    if (pathname.startsWith('/quiz')) return 'Personality Quizzes';
    if (pathname.startsWith('/reward/redemptions')) return 'Redemptions';
    if (pathname.startsWith('/reward')) return 'Rewards';
    if (pathname.startsWith('/donation')) return 'Donation History';
    return 'Dashboard';
};

const Header: FC = () => {
    const location = useLocation();
    const history = useHistory();
    const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const userData = UserAuthService.getData();
    const rawUsername = userData?.username;
    const username = !rawUsername || rawUsername.toLowerCase() === 'firstname' ? 'Admin User' : rawUsername;

    const initials = useMemo(() => {
        const parts = username.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return username.slice(0, 2).toUpperCase() || 'AU';
    }, [username]);

    // Handle outside clicks to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [dropdownOpen]);

    const handlePromptLogout = useCallback(() => {
        setDropdownOpen(false);
        setShowLogoutModal(true);
    }, []);

    const handleConfirmLogout = useCallback(() => {
        setLoggingOut(true);
        UserAuthService.logout().then(() => {
            setLoggingOut(false);
            setShowLogoutModal(false);
            history.push(Router.generate(routes.LOGIN));
        });
    }, [history]);

    return (
        <header
            className={classNames(
                styles.header,
                'main-header',
                'navbar',
                'navbar-expand',
                'navbar-white',
                'navbar-light',
            )}
        >
            {/* Left section: Toggle & Title */}
            <div className={styles.leftSection}>
                <Navigation />
                <h1 className={styles.pageTitle}>{pageTitle}</h1>
            </div>

            {/* Right section: Profile Dropdown with Logout */}
            <div className={styles.rightSection}>
                <div className={styles.profileWrapper} ref={dropdownRef}>
                    <button
                        type="button"
                        className={classNames(styles.profileBtn, { [styles.active]: dropdownOpen })}
                        onClick={() => setDropdownOpen((prev) => !prev)}
                        aria-expanded={dropdownOpen}
                        aria-label="User Profile Menu"
                    >
                        <div className={styles.userAvatar}>{initials}</div>
                        <div className={styles.profileMeta}>
                            <span className={styles.profileName}>{username}</span>
                            <span className={styles.profileRole}>Admin</span>
                        </div>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={classNames(styles.chevronIcon, { [styles.chevronRotated]: dropdownOpen })}
                        />
                    </button>

                    {dropdownOpen && (
                        <div className={styles.dropdownMenu}>
                            <button
                                type="button"
                                className={classNames(styles.dropdownItem, styles.logoutItem)}
                                onClick={handlePromptLogout}
                            >
                                <FontAwesomeIcon icon={faSignOutAlt} className={styles.dropdownItemIcon} />
                                <span>Log Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <LogoutConfirmationModal
                show={showLogoutModal}
                onHide={() => setShowLogoutModal(false)}
                onConfirm={handleConfirmLogout}
                loading={loggingOut}
            />
        </header>
    );
};

export default memo(Header);
